// components/pages/SectionPage.js - Updated with theme support
import './SectionPage.css';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import FeedSection from '../feed/FeedSection';
import MaterialsFeedSection from '../feed/MaterialsFeedSection';
import MultiDepartmentSection from '../feed/MultiDepartmentSection';
import MobileFeedCarousel from '../feed/MobileFeedCarousel';
import MembersCarousel from '../members/MembersCarousel';
import MembersCarouselSection from '../members/MembersCarouselSection';
import DeskCarousel from '../carousel/DeskCarousel';
import { MEMBER_TYPES } from '../../utils/membersConfig';
import { isCommitteeFeed } from '../../utils/committeeFeedHelpers';

import { getMobileFeedsForSection } from '../../utils/feedsTransformer';
import NotificationButton from '../notifications/NotificationButton';
import SearchNavBar from '../search/SearchNavBar';
import MobileNavigation from '../navigation/MobileNavigation';
import SectionMobileCarousel from '../carousel/SectionCarousel';
import openCapacitorSite from '../../utils/browserUtils';
const FILTER_BAR_OFFSET = 34; // height the inline feed filter row adds to the home navbar
const SectionPage = ({
  country = 'us',
  sectionKey,
  sectionInfo = {},
  feedsConfig = {},
  maxItemsPerFeed = 1,
  carouselInterval = 5000,
  useMobileLayout = null,
  isDepartmentPage = false,
  departmentKey = null
}) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (useMobileLayout !== null) return useMobileLayout;
    if (typeof window === 'undefined') return false; // SSR safety
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
    const isSmallScreen = window.innerWidth <= 768;
    return isMobileDevice || isSmallScreen;
  });

  // Helper functions to get feeds by type from the passed feedsConfig
  const getFeedsByType = (sectionKey, type = 'normal') => {
    const sectionFeeds = feedsConfig[sectionKey] || [];
    return sectionFeeds.filter(feed => feed.type === type);
  };

  const getNormalFeeds = (sectionKey) => {
    return getFeedsByType(sectionKey, 'normal');
  };

  const getCarouselFeeds = (sectionKey) => {
    return getFeedsByType(sectionKey, 'carousel');
  };

  /**
   * Determine if this section should show members carousels
   * Returns an array of member carousel configs
   */
  const getMembersCarouselConfigs = () => {
    if (country !== 'us') return [];

    switch (sectionKey) {
      case 'judicial':
        return [{
          memberType: MEMBER_TYPES.JUSTICE,
          title: 'Supreme Court Justices',
          limit: 9,
          viewAllLink: '/us/justices',
          viewAllText: 'View All Justices'
        }];
      default:
        return [];
    }
  };

  const membersCarouselConfigs = getMembersCarouselConfigs();

  // Auto-detect mobile layout
  useEffect(() => {
    if (useMobileLayout !== null) {
      setIsMobile(useMobileLayout);
      return;
    }

    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, [useMobileLayout]);

  // Map section key to proper branch for notification topics
  const getSectionBranch = (sectionKey) => {
    switch (sectionKey) {
      case 'legislative':
        return 'legislative';
      case 'executive':
        return 'executive';
      case 'judicial':
        return 'judicial';
      case 'defense':
      case 'defence':
        return 'defense';
      case 'economy':
        return 'economy';
      case 'environment':
        return 'environment';
      case 'foreign-affairs':
      case 'foreignaffairs':
      case 'foreignAffairs':
        return 'foreign affairs';
      case 'health':
        return 'health';
      case 'elections':
        return 'elections';
      case 'science-tech':
      case 'sciencetech':
      case 'science':
        return 'science & tech';
      case 'spending':
        return 'spending';
      case 'infrastructure':
        return 'infrastructure';
      case 'regulations':
        return 'regulations';
      case 'justice':
        return 'justice';
      default:
        return sectionKey;
    }
  };

  // Handle section-level notification state changes
  const handleSectionNotificationChange = (isSubscribed, feedId, feedName, branch, topic) => {
    // console.log(`Section notification ${isSubscribed ? 'enabled' : 'disabled'}:`, {
    //   section: sectionKey,
    //   country: country,
    //   departmentKey: departmentKey,
    //   feedId, feedName, branch, topic
    // });
  };

  // Extract section info. The header is title + notification button only; the
  // description and the image/icon avatar were dropped, but learnMoreLink stays
  // so people can still open the official about page for the section.
  const {
    title = sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1),
    learnMoreLink = ""
  } = sectionInfo;

  const openLearnMore = (e) => {
    e.preventDefault();
    openCapacitorSite(learnMoreLink, "_blank");
  };

  // Generate section-level notification ID and determine proper branch
  const sectionBranch = getSectionBranch(sectionKey);
  const sectionNotificationId = isDepartmentPage ?
    `dept-${country}-${departmentKey}` :
    `${sectionKey}-branch-${country}`;
  const sectionNotificationName = isDepartmentPage ?
    `${title} Updates` :
    `${title} Updates`;

  /**
   * Render the appropriate feed component based on feed type
   */
  const renderFeedComponent = (feed) => {
    if (isCommitteeFeed(feed)) {
      return (
        <MaterialsFeedSection
          title={feed.title}
          apiEndpoint={feed.apiEndpoint}
          feedName={feed.feedName}
          branch={feed.branch}
          topic={feed.topic || 'N/A'}
          buttonName={feed.buttonName}
          maxItems={maxItemsPerFeed}
        />
      );
    }

    return (
      <FeedSection
        title={feed.title}
        apiEndpoint={feed.apiEndpoint}
        feedName={feed.feedName}
        branch={feed.branch}
        topic={feed.topic || 'N/A'}
        buttonName={feed.buttonName}
        viewAllLink={feed.viewAllLink}
        displayFunction={feed.displayFunction}
        maxItems={maxItemsPerFeed}
        enableNotifications={false}
      />
    );
  };

  // Mobile Layout
  if (isMobile) {
    const mobileFeeds = getMobileFeedsForSection(feedsConfig, sectionKey, country);

    const sectionFeeds = feedsConfig[sectionKey] || [];
    const hasCarouselFeeds = sectionFeeds.some(feed => feed.type === 'carousel');

    return (
      <div id='homebg' className="section-page-root">
        <div style={{ marginTop: '0px', paddingTop: '0px' }}>

          {/* Section header - title, notification button, learn more link */}
          <div className="mobile-section-header" style={{ padding: '5px 15px', marginBottom: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {/* Spacer keeps the title optically centered against the button */}
              <div style={{ width: '40px', flexShrink: 0 }} />

              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '17px', fontWeight: '600', flex: 1, textAlign: 'center' }}>{title}</h3>

              <div style={{ width: '40px', display: 'flex', justifyContent: 'flex-end' }}>
                <NotificationButton
                  feedId={sectionNotificationId}
                  feedName={sectionNotificationName}
                  branch={sectionBranch}
                  topic={isDepartmentPage ? "Department" : sectionBranch}
                  country={country}
                  variant="outline-light"
                  size="sm"
                  showLabel={false}
                  onNotificationStateChange={handleSectionNotificationChange}
                />
              </div>
            </div>

            {learnMoreLink && (
              <div style={{ textAlign: 'center', marginTop: '4px' }}>
                <Link
                  to="#"
                  onClick={openLearnMore}
                  style={{ color: 'var(--accent-hover)', fontSize: '11px' }}
                >
                  Learn more
                </Link>
              </div>
            )}
          </div>

          {/* MEMBERS CAROUSELS - Mobile */}
          {membersCarouselConfigs.map((config, index) => (
            <div key={`members-carousel-${index}`} className="section-carousel-wrapper section-members-carousel">
              <MembersCarousel
                memberType={config.memberType}
                limit={config.limit}
                title={config.title}
                showHeading={true}
                showViewAll={true}
                viewAllLink={config.viewAllLink}
                viewAllText={config.viewAllText}
              />
            </div>
          ))}

          {/* CONDITIONAL COMPONENT SELECTION */}
          {hasCarouselFeeds ? (
            <div className="section-carousel-wrapper mobile-feeds-container">
              <MobileFeedCarousel feeds={mobileFeeds} />
            </div>
          ) : (
            mobileFeeds.length > 0 ? (
              <div className="section-carousel-wrapper">
                <SectionMobileCarousel
                  feeds={mobileFeeds}
                  sectionTitle={title}
                />
              </div>
            ) : (
              <div className="no-feeds-message">
                <p style={{ color: 'var(--text-primary)' }}>No feeds available for {title}</p>
                <p style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Section: {sectionKey}, Country: {country}
                </p>
              </div>
            )
          )}
        </div>
      </div>
    );
  }

  // Desktop Layout
  const normalFeeds = getNormalFeeds(sectionKey);
  const carouselFeeds = getCarouselFeeds(sectionKey);

  const regularFeeds = normalFeeds.filter(feed => !isCommitteeFeed(feed));
  const committeeFeeds = normalFeeds.filter(feed => isCommitteeFeed(feed));

  const committeeFeedRows = [];
  for (let i = 0; i < committeeFeeds.length; i += 2) {
    committeeFeedRows.push(committeeFeeds.slice(i, i + 2));
  }

  /**
   * Generate feed variations with different skip values for carousel display
   */
  const generateFeedVariations = (baseFeed, count = 10) => {
    const variations = [];
    for (let i = 0; i < count; i++) {
      let endpoint = baseFeed.apiEndpoint;

      if (endpoint.includes('skip=')) {
        endpoint = endpoint.replace(/skip=\d+/, `skip=${i}`);
      } else {
        endpoint += `&skip=${i}`;
      }

      variations.push({
        ...baseFeed,
        id: `${baseFeed.id}-${i}`,
        apiEndpoint: endpoint,
        title: baseFeed.title
      });
    }
    return variations;
  };

  const regularFeedRows = [];
  for (let i = 0; i < regularFeeds.length; i += 2) {
    regularFeedRows.push(regularFeeds.slice(i, i + 2));
  }

  return (
    <div id='homebg' className="section-page-root">
      <div style={{ height: '80px' }}></div>

      {/* Section header - title, notification button, learn more link */}
      <div style={{ padding: '30px 60px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: learnMoreLink ? '10px' : '0'
        }}>
          <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>{title}</h3>

          <NotificationButton
            feedId={sectionNotificationId}
            feedName={sectionNotificationName}
            branch={sectionBranch}
            topic={isDepartmentPage ? "Department" : sectionBranch}
            country={country}
            variant="outline-light"
            size="sm"
            showLabel={true}
            onNotificationStateChange={handleSectionNotificationChange}
          />
        </div>

        {learnMoreLink && (
          <div style={{ fontSize: '16px' }}>
            <Link
              to="#"
              style={{ color: 'var(--accent-hover)' }}
              onClick={openLearnMore}
            >
              Learn more
            </Link>
          </div>
        )}
      </div>

      <br /><br />

      {/* MEMBERS CAROUSELS - Desktop */}
      {membersCarouselConfigs.length > 0 && (
        <div id='branchPage' style={{ marginBottom: '1rem' }}>
          <Container className='bgcolor'>
            <Container id='carouselBufferOne'>
              {membersCarouselConfigs.map((config, index) => (
                <Row key={`members-section-${index}`}>
                  <Col md={12}>
                    <MembersCarouselSection
                      memberType={config.memberType}
                      limit={config.limit}
                      title={config.title}
                      showViewAll={true}
                      viewAllLink={config.viewAllLink}
                    />
                  </Col>
                </Row>
              ))}
            </Container>
          </Container>
        </div>
      )}

      {/* Regular feeds - each feed gets its own DeskCarousel */}
      {regularFeedRows.map((row, rowIndex) => (
        <div key={`feed-row-${rowIndex}`} id='branchPage' style={{ marginBottom: '1rem' }}>
          <Container className='bgcolor'>
            <Container id='carouselBufferOne'>
              <Row>
                {row.map((feed, colIndex) => (
                  <Col key={feed.id} md={6}>
                    <DeskCarousel
                      title={feed.title}
                      sections={generateFeedVariations(feed, 10)}
                      viewAllLink={feed.viewAllLink || `/${country}/${sectionKey}`}
                      intervalDuration={carouselInterval}
                      showAll={true}
                      showFeedTitle={false}
                    />
                  </Col>
                ))}
              </Row>
            </Container>
          </Container>
        </div>
      ))}

      {/* Committee feeds in rows */}
      {committeeFeedRows.map((row, rowIndex) => (
        <div key={`committee-${rowIndex}`} id='branchPage' style={{ marginBottom: '1rem' }}>
          <Container className='bgcolor'>
            <Container id='carouselBufferOne'>
              <Row>
                {row.map((feed, colIndex) => (
                  <Col key={colIndex} id={colIndex % 2 === 0 ? 'legislativeColLeft' : 'legislativeColRight'}>
                    <div id={colIndex % 2 === 0 ? 'columnLefttPadding' : 'columnRightPadding'}>
                      <MaterialsFeedSection
                        title={feed.title}
                        apiEndpoint={feed.apiEndpoint}
                        feedName={feed.feedName}
                        branch={feed.branch}
                        topic={feed.topic || 'N/A'}
                        buttonName={feed.buttonName}
                        maxItems={maxItemsPerFeed}
                      />
                    </div>
                  </Col>
                ))}

                {row.length === 1 && (
                  <Col id='legislativeColRight'>
                    <div id='columnRightPadding'></div>
                  </Col>
                )}
              </Row>
            </Container>
          </Container>
        </div>
      ))}

      {carouselFeeds.length > 0 && (
        <MultiDepartmentSection
          departments={carouselFeeds}
          departmentsPerRow={2}
          carouselInterval={carouselInterval}
          maxItemsPerFeed={maxItemsPerFeed}
        />
      )}
    </div>
  );
};

export default SectionPage;